import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { refreshAccessToken } from '../services/api';
import {
  SESSION_END_MARGIN_SECONDS,
  canRenewAccessToken,
  clearSession,
  getAccessToken,
  getLastActivityAt,
  getSharedSessionEndReason,
  getTokenExp,
  isSessionOver,
  isTokenExpired,
  sanitizeStoredUser,
  saveSession,
  setSessionEndReason,
  touchLastActivity,
} from '../services/authStorage';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  normalizeUnitId,
  getUserUnits,
  getUserUnitIds,
  getUserUnitId,
  hasFixedUnit as hasFixedUnitScope,
  hasMultipleUnits as hasMultipleUnitsScope,
  isGlobalUnitUser,
  getEffectiveUnitId,
  resolveDefaultActiveUnitId,
  buildReadParams,
  requireWriteUnit as requireWriteUnitScope,
} from '../utils/unitScope';

const UsersContext = createContext();
const TAB_CLOSE_MARKER_KEY = 'siapen:tab_closed_marker';
const TAB_RELOAD_FLAG_KEY = 'siapen:tab_reloading';
const TAB_ID_KEY = 'siapen:tab_id';
// Sem atividade em nenhuma aba por esse tempo, a sessão é encerrada como no botão "Sair".
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;
// A última atividade vai pro localStorage no máximo uma vez nesse intervalo.
const ACTIVITY_WRITE_INTERVAL_MS = 15 * 1000;
// Rotas abertas: sem checagem de sessão nem de inatividade.
const PUBLIC_ROUTES = ['/login', '/cadastro-visitante', '/cadastro-advogado', '/politica-de-privacidade'];

// Perfis externos somente-leitura (acesso restrito a /internos + tela de detalhes externa).
// Ao liberar um novo perfil externo com esse mesmo comportamento, acrescentar o grupo aqui.
const EXTERNAL_PROFILE_GROUPS = ['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA', 'MPPI'];

const LOGIN_LOCKED_FALLBACK = 'Sua conta está bloqueada por excesso de tentativas de login. Entre em contato com o administrador do sistema.';

// Header Retry-After (segundos ou HTTP-date) -> ms. null se ausente/invalido.
const parseRetryAfterMs = (headerValue) => {
  if (headerValue === undefined || headerValue === null || headerValue === '') return null;
  const asSeconds = Number(headerValue);
  if (Number.isFinite(asSeconds) && asSeconds >= 0) return Math.round(asSeconds * 1000);
  const asDate = Date.parse(headerValue);
  if (!Number.isNaN(asDate)) return Math.max(0, asDate - Date.now());
  return null;
};

// Remove o prefixo "error:" repetido que o backend as vezes manda ("error: error: ...").
const stripErrorPrefix = (message) => String(message || '').replace(/^(\s*error:\s*)+/i, '').trim();

// Texto cru do corpo do erro, só pra checar se e mensagem de bloqueio — nao usar pra exibir
// (quem exibe e getApiErrorMessage/mensagem generica, conforme o caso).
const extractBackendText = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.message === 'string') return data.message;
  if (typeof data.detail === 'string') return data.detail;
  return '';
};

// So e bloqueio de verdade quando o texto do backend diz isso — nao dá pra confiar só no
// status HTTP (o 401 as vezes tambem vem em senha errada comum, nao só em conta bloqueada).
const isAccountLockedMessage = (data) => /bloque/i.test(extractBackendText(data));

// Mapeia o erro do login para uma unica mensagem, sem vazar o corpo do backend (senha errada /
// usuario inexistente viram a mesma mensagem generica — mata a enumeracao de contas). Conta
// bloqueada e identificada pelo texto da mensagem, nao só pelo status.
const mapLoginError = (error) => {
  const response = error?.response;
  if (!response) {
    return {
      message: 'Sem conexão com o servidor. Verifique sua internet e tente novamente.',
      reason: 'network',
      retryAfterMs: null,
    };
  }

  const status = response.status;
  if (isAccountLockedMessage(response.data)) {
    return { message: LOGIN_LOCKED_FALLBACK, reason: 'locked', retryAfterMs: null };
  }
  if (status === 429) {
    return {
      message: 'Muitas tentativas de login. Aguarde alguns instantes.',
      reason: 'rate_limit',
      retryAfterMs: parseRetryAfterMs(response.headers?.['retry-after']),
    };
  }
  if (status === 400 || status === 401 || status === 403 || status === 404) {
    return { message: 'E-mail ou senha inválidos.', reason: 'invalid', retryAfterMs: null };
  }
  if (status >= 500) {
    return { message: 'Falha no servidor. Tente novamente em instantes.', reason: 'server', retryAfterMs: null };
  }
  return { message: 'Não foi possível fazer login. Tente novamente.', reason: 'unknown', retryAfterMs: null };
};

export function useUsers() {
  return useContext(UsersContext);
}

export function UsersProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();  // Para verificar qual rota está sendo acessada

  const [userInfo, setUserInfo] = useState(() => {
    const savedUserInfo = localStorage.getItem('user');
    return savedUserInfo ? JSON.parse(savedUserInfo) : null;
  });
  // Unidade ativa usada por usuario sem unidade fixa.
  const [activeUnitIdState, setActiveUnitIdState] = useState(() => {
    const savedUnitId = localStorage.getItem('activeUnitId');
    return normalizeUnitId(savedUnitId);
  });

  const isAuthenticated = !!getAccessToken();

  const clearAuthData = useCallback(() => {
    clearSession();
  }, []);

  // Atualiza unidade ativa no estado e persiste em localStorage.
  const setActiveUnitId = useCallback((unitId) => {
    const normalized = normalizeUnitId(unitId);
    setActiveUnitIdState(normalized);

    if (normalized === null) {
      localStorage.removeItem('activeUnitId');
      return;
    }

    localStorage.setItem('activeUnitId', String(normalized));
  }, []);

  // Variaveis centrais de escopo de unidade.
  const userUnits = getUserUnits(userInfo);
  const userUnitIds = getUserUnitIds(userInfo);
  const userUnitId = getUserUnitId(userInfo);
  const hasFixedUnit = hasFixedUnitScope(userInfo);
  const hasMultipleUnits = hasMultipleUnitsScope(userInfo);
  const effectiveUnitId = getEffectiveUnitId(userInfo, activeUnitIdState);

  // Builder padrao para filtros de leitura sem enviar unit nula.
  const buildUnitReadParams = useCallback((params = {}) => (
    buildReadParams(params, userInfo, activeUnitIdState)
  ), [userInfo, activeUnitIdState]);

  // Validador padrao para escrita quando unidade e obrigatoria.
  const requireWriteUnit = useCallback(() => (
    requireWriteUnitScope(userInfo, activeUnitIdState)
  ), [userInfo, activeUnitIdState]);

  // Função para verificar a validade do token
  const isTokenValid = useCallback(() => {
    const accessToken = getAccessToken();
    if (!accessToken) return false; // Se não houver token, não é válido
    return !isTokenExpired(accessToken);
  }, []);

  const sessionTimerRef = useRef(null);

  // reason: só nos logouts automáticos ('inactivity' | 'expired' | 'ended'), pra tela de login avisar o
  // usuário. O botão "Sair" chama sem motivo e não mostra aviso.
  const logout = useCallback(async ({ reason } = {}) => {
    try {
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      if (reason) setSessionEndReason(reason);

      // Avisa o backend pra invalidar o token (e o cookie dos relatórios). Não espera a resposta:
      // a sessão local é limpa de qualquer jeito, mesmo sem rede. O header vai explícito porque
      // o token sai do localStorage logo em seguida.
      const accessToken = getAccessToken();
      if (accessToken && !isTokenExpired(accessToken)) {
        api.post('users/user/logout/', null, {
          headers: { Authorization: `Bearer ${accessToken}` },
          skip401Redirect: true,
          withCredentials: true,
          timeout: 10000,
        }).catch(() => {});
      }

      clearAuthData();
      localStorage.removeItem(TAB_CLOSE_MARKER_KEY);
      sessionStorage.removeItem(TAB_RELOAD_FLAG_KEY);
      setUserInfo(null);
      navigate('/login');
    } catch (error) {
      console.error('Erro no logout:', error);
      toast.error('Erro ao tentar fazer logout');
    }
  }, [clearAuthData, navigate]);

  useEffect(() => {
    // Inicializa id da aba para controle de ciclo da sessao.
    const existingTabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!existingTabId) {
      sessionStorage.setItem(TAB_ID_KEY, `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    }

    // Se existir marcador de fechamento e nao for reload da mesma aba, encerra sessao.
    const closeMarker = localStorage.getItem(TAB_CLOSE_MARKER_KEY);
    const isReloading = sessionStorage.getItem(TAB_RELOAD_FLAG_KEY) === '1';

    if (closeMarker) {
      localStorage.removeItem(TAB_CLOSE_MARKER_KEY);
      if (!isReloading) {
        clearAuthData();
        setUserInfo(null);
      }
    }

    // Limpa flag de reload para a aba atual apos montar.
    sessionStorage.removeItem(TAB_RELOAD_FLAG_KEY);

    const markTabClosing = () => {
      try {
        const currentTabId = sessionStorage.getItem(TAB_ID_KEY);
        sessionStorage.setItem(TAB_RELOAD_FLAG_KEY, '1');
        localStorage.setItem(
          TAB_CLOSE_MARKER_KEY,
          JSON.stringify({ tabId: currentTabId, at: Date.now() })
        );
      } catch (error) {
        // noop
      }
    };

    window.addEventListener('beforeunload', markTabClosing);

    return () => {
      window.removeEventListener('beforeunload', markTabClosing);
    };
  }, [clearAuthData]);

  const inactivityTimerRef = useRef(null);
  const lastActivityWriteRef = useRef(0);

  // Inatividade conta o navegador inteiro: qualquer aba grava a última atividade no localStorage e o
  // timer relê esse valor antes de agir. Passado o limite, desloga como o botão "Sair".
  const checkInactivity = useCallback(() => {
    const check = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (!getAccessToken()) return;

      let lastActivityAt = getLastActivityAt();
      if (!lastActivityAt) {
        // Sessão aberta antes desse controle existir: começa a contar agora.
        touchLastActivity();
        lastActivityAt = Date.now();
      }

      const idleMs = Date.now() - lastActivityAt;
      if (idleMs >= INACTIVITY_LIMIT_MS) {
        logout({ reason: 'inactivity' });
        return;
      }

      inactivityTimerRef.current = setTimeout(check, INACTIVITY_LIMIT_MS - idleMs);
    };

    check();
  }, [logout]);

  // Timer único da sessão: SESSION_END_MARGIN_SECONDS antes de o token vencer, renova (só se o refresh
  // vencer depois dele) ou encerra a sessão como o botão "Sair". Se outra aba já renovou, só reagenda.
  const scheduleSessionRenewal = useCallback(() => {
    const schedule = () => {
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);

      const exp = getTokenExp(getAccessToken());
      if (!exp) return;

      const delayMs = Math.max(0, (exp - SESSION_END_MARGIN_SECONDS - Date.now() / 1000) * 1000);

      sessionTimerRef.current = setTimeout(async () => {
        const currentToken = getAccessToken();
        if (!currentToken) return;

        if (!isTokenExpired(currentToken, SESSION_END_MARGIN_SECONDS)) {
          schedule();
          return;
        }

        if (canRenewAccessToken()) {
          try {
            await refreshAccessToken(currentToken);
            schedule();
            return;
          } catch (error) {
            console.error('Erro ao renovar sessão:', error);
          }
        }

        logout({ reason: 'expired' });
      }, delayMs);
    };

    schedule();
  }, [logout]);

  useEffect(() => {
    scheduleSessionRenewal();
    return () => {
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    };
  }, [scheduleSessionRenewal]);

  // Verificar a sessão a cada troca de rota (exceto nas rotas públicas) e redirecionar se necessario.
  useEffect(() => {
    if (PUBLIC_ROUTES.includes(location.pathname)) return;

    // Token vencido que ainda dá pra renovar (ex.: computador voltando da suspensão): renova antes.
    const accessToken = getAccessToken();
    if (accessToken && isTokenExpired(accessToken) && canRenewAccessToken()) {
      refreshAccessToken(accessToken)
        .then(() => scheduleSessionRenewal())
        .catch(() => logout({ reason: 'expired' }));
      return;
    }

    // Sem token, ou a SESSION_END_MARGIN_SECONDS (ou menos) de o token vencer sem renovação: encerra.
    // Só avisa "sessão expirada" se havia sessão (quem nem logou vai pro login sem aviso).
    if (isSessionOver()) {
      logout(accessToken ? { reason: 'expired' } : undefined); // Redireciona para o login
    }
  }, [location.pathname, logout, scheduleSessionRenewal]); // Dependências

  useEffect(() => {
    if (PUBLIC_ROUTES.includes(location.pathname)) return;
    if (!isAuthenticated) return;

    // Atividade em qualquer aba conta: só grava o horário (no máximo a cada ACTIVITY_WRITE_INTERVAL_MS).
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityWriteRef.current < ACTIVITY_WRITE_INTERVAL_MS) return;
      lastActivityWriteRef.current = now;
      touchLastActivity();
    };
    // Voltar pra aba não zera o contador: confere quanto tempo ficou parado (com a aba em segundo plano
    // ou o computador suspenso, o timer pode ter atrasado).
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkInactivity();
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibility);

    checkInactivity();

    return () => {
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isAuthenticated, location.pathname, checkInactivity]);

  // Sincroniza as abas: logout numa aba leva as outras pro login; login numa aba atualiza as outras.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.storageArea !== localStorage) return;

      if ((event.key === 'accessToken' || event.key === null) && !getAccessToken()) {
        // A outra aba já chamou a rota de logout (ou a sessão já foi recusada): aqui só limpa, com o
        // mesmo aviso dela (nenhum, se foi pelo botão "Sair").
        if (PUBLIC_ROUTES.includes(window.location.pathname)) {
          setUserInfo(null);
          return;
        }
        logout({ reason: getSharedSessionEndReason() });
        return;
      }

      if (event.key === 'user' && event.newValue) {
        try {
          setUserInfo(JSON.parse(event.newValue));
        } catch (error) {
          // mantém o usuário atual
        }
        scheduleSessionRenewal();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [logout, scheduleSessionRenewal]);

  // Mantem unidade ativa sincronizada para usuario com unidade fixa.
  useEffect(() => {
    if (!userInfo) {
      if (activeUnitIdState !== null) {
        setActiveUnitId(null);
      }
      return;
    }

    const resolvedActiveUnitId = resolveDefaultActiveUnitId(userInfo, activeUnitIdState);

    if (String(activeUnitIdState ?? '') !== String(resolvedActiveUnitId ?? '')) {
      setActiveUnitId(resolvedActiveUnitId);
    }
  }, [userInfo, activeUnitIdState, setActiveUnitId]);

  // Retorna { ok, reason, retryAfterMs } para a tela de login decidir o que fazer.
  // reason: null (sucesso) | 'invalid' | 'locked' | 'rate_limit' | 'network' | 'server' | 'unknown'.
  const login = async (email, password) => {
    try {
      // skip401Redirect: um 401 no login é erro de negócio (credencial inválida/conta bloqueada),
      // não expiração de sessão — não deve disparar o reload global do interceptor.
      // timeout só nesta request para o caso "sem conexão" disparar sem afetar relatórios/downloads.
      // withCredentials: pedido pelo backend para o navegador aceitar/guardar o cookie de sessão
      // que a tela de Relatórios usa depois (iframe apontando pro domínio da API — não tem como
      // mandar o header Authorization num iframe, então aquela tela depende desse cookie).
      // skipAuth: rota pública — um token antigo que sobrou no navegador iria no header e a API
      // recusaria o próprio login (401 token_not_valid, que aparecia como "E-mail ou senha inválidos").
      const response = await api.post(
        'users/user/login/',
        { email, password },
        // { skip401Redirect: true, timeout: 15000},
        // skipRateLimitNotice: o 429 do login já tem contagem regressiva na própria tela.
        { skip401Redirect: true, timeout: 15000, withCredentials: true, skipAuth: true, skipRateLimitNotice: true },
      );
      const data = response.data;

      if (data && data.token && data.user) {
        // Normaliza grupos no login para evitar quebra quando API nao retorna array.
        const loginGroups = Array.isArray(data.user?.groups)
          ? data.user.groups
          : (typeof data.user?.groups === 'string' ? [data.user.groups] : []);
        // Guarda token, refresh e usuário (sem os dados pessoais que nenhuma tela usa).
        saveSession({ access: data.token, refresh: data.refresh, user: data.user });
        localStorage.removeItem(TAB_CLOSE_MARKER_KEY);
        sessionStorage.removeItem(TAB_RELOAD_FLAG_KEY);

        setUserInfo(sanitizeStoredUser(data.user));
        setActiveUnitId(resolveDefaultActiveUnitId(data.user, null));
        scheduleSessionRenewal();

        if (loginGroups.includes('ADVOGADO(A)')) {
          navigate('/advogado');
          toast.success('Seja bem-vindo, Advogado!');
        } else if (EXTERNAL_PROFILE_GROUPS.some((group) => loginGroups.includes(group))) {
          navigate('/internos');
          toast.success('Seja bem-vindo!');
        } else {
          navigate('/');
          toast.success('Seja bem-vindo!');
        }
        return { ok: true, reason: null, retryAfterMs: null };
      }

      // 200 sem token/user (improvável pelo contrato atual): trata como credencial inválida.
      toast.error('E-mail ou senha inválidos.');
      return { ok: false, reason: 'invalid', retryAfterMs: null };
    } catch (error) {
      const mapped = mapLoginError(error);
      const message = mapped.reason === 'locked'
        ? (stripErrorPrefix(getApiErrorMessage(error)) || LOGIN_LOCKED_FALLBACK)
        : mapped.message;
      // 429: quem mostra a contagem regressiva é a tela de login, então não duplica o toast aqui.
      if (mapped.reason !== 'rate_limit') {
        toast.error(message);
      }
      return { ok: false, reason: mapped.reason, retryAfterMs: mapped.retryAfterMs };
    }
  };

  const listUsers = async () => {
    try {
      if (!isTokenValid()) {
        logout();
        return;
      }

      const accessToken = localStorage.getItem('accessToken');
      const response = await api.get('users/user/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      toast.error('Erro ao buscar usuários');
    }
  };

  // Extrai a mensagem de erro real vinda da API (mesmo padrão usado em internalContext.js/penalExecutionContext.js).
  const getApiErrorMessage = (error) => {
    const data = error?.response?.data;
    if (!data) return '';

    if (typeof data === 'string' && data.trim()) {
      const raw = data.trim();
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.message === 'string' && parsed.message.trim()) {
          return parsed.message;
        }
        if (typeof parsed?.detail === 'string' && parsed.detail.trim()) {
          return parsed.detail;
        }
      } catch (_) {
        // mantém fallback abaixo com string original
      }
      return raw;
    }

    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (Array.isArray(data?.message) && data.message.length > 0) {
      return String(data.message[0]);
    }
    if (typeof data?.message === 'object' && data.message !== null) {
      for (const value of Object.values(data.message)) {
        if (Array.isArray(value) && value.length > 0) {
          return String(value[0]);
        }
        if (typeof value === 'string' && value.trim()) {
          return value;
        }
      }
    }

    if (typeof data?.detail === 'string' && data.detail.trim()) {
      return data.detail;
    }

    if (typeof data?.error === 'string' && data.error.trim()) {
      return data.error;
    }

    if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length > 0) {
      return String(data.non_field_errors[0]);
    }

    if (typeof data === 'object') {
      for (const value of Object.values(data)) {
        if (Array.isArray(value) && value.length > 0) {
          return String(value[0]);
        }
        if (typeof value === 'string' && value.trim()) {
          return value;
        }
      }
    }

    return '';
  };

  // Envia o código de verificação por email para o usuário logado (identificado via JWT).
  const sendCode = useCallback(async () => {
    try {
      await api.post('users/user/changepassword/create/');
      toast.success('Código enviado para seu email');
      return true;
    } catch (error) {
      console.error('Erro ao enviar código:', error);
      toast.error(getApiErrorMessage(error) || 'Erro ao enviar código');
      return false;
    }
  }, []);

  // Verifica o código enviado por email. Backend pode responder 200 mesmo em erro
  // (código invalido/expirado), entao o corpo da resposta tambem precisa ser inspecionado.
  const checkCode = useCallback(async (code) => {
    try {
      const response = await api.post('users/user/changepassword/check/', { code });
      const data = response.data;
      if (!data || data.detail !== 'Code verified successfully.') {
        toast.error(getApiErrorMessage({ response: { data } }) || 'Código inválido ou expirado');
        return false;
      }
      toast.success('Código validado com sucesso');
      return true;
    } catch (error) {
      console.error('Erro ao verificar código:', error);
      toast.error(getApiErrorMessage(error) || 'Código inválido ou expirado');
      return false;
    }
  }, []);

  // Efetiva a nova senha apos o código verificado. skip401Redirect evita que um 401
  // de regra de negocio (codigo ainda nao verificado) derrube a sessao do usuario.
  const changePassword = useCallback(async (password) => {
    try {
      const response = await api.patch(
        'users/user/changepassword/change/',
        { password },
        { skip401Redirect: true }
      );
      const data = response.data;
      if (!data || data.user !== 'change password') {
        toast.error(getApiErrorMessage({ response: { data } }) || 'Erro ao alterar senha');
        return false;
      }
      toast.success('Senha alterada com sucesso');
      return true;
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      toast.error(getApiErrorMessage(error) || 'Erro ao alterar senha');
      return false;
    }
  }, []);

  return (
    <UsersContext.Provider
      value={{
        login,
        logout,
        isTokenValid,
        userInfo,
        setUserInfo,
        listUsers,
        isAuthenticated,
        userUnits,
        userUnitIds,
        userUnitId,
        hasFixedUnit,
        hasMultipleUnits,
        isGlobalUnitUser: isGlobalUnitUser(userInfo),
        activeUnitId: activeUnitIdState,
        setActiveUnitId,
        effectiveUnitId,
        buildUnitReadParams,
        requireWriteUnit,
        sendCode,
        checkCode,
        changePassword,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
}
